/**
 * NOVA-038 — the client-side export serialisers. The server returns the AGGREGATE row set (GET
 * /analytics/export, audited-on-read); this module renders it to JSON / CSV / XLSX and triggers a browser
 * download. AGGREGATE-ONLY by construction — it serialises only the (scope, metric_type, discipline_code,
 * value, count, …) columns the server sent; there is no per-patient line, NIN or name anywhere (AN-2/L-4).
 *
 * Dependency-free. CSV is RFC-4180 quoted; the "xlsx" format is an Excel-native SpreadsheetML 2003 XML
 * workbook (the `.xls`/`application/vnd.ms-excel` flavour Excel + LibreOffice open directly) — chosen over
 * pulling a zip/xlsx package into the bundle for a read-only aggregate export.
 */
import type { ExportData, ExportFormat, ExportRow } from "./types";

/** The fixed column order of the flat export table (stable across formats). */
const COLUMNS = ["scope", "metric_type", "discipline_code", "value", "count", "date", "medecin_id"] as const;
type Col = (typeof COLUMNS)[number];

function cell(row: ExportRow, col: Col): string {
  const v = (row as unknown as Record<string, unknown>)[col];
  if (v === undefined || v === null) return "";
  return String(v);
}

// ── JSON ───────────────────────────────────────────────────────────────────────
function toJson(data: ExportData): string {
  return JSON.stringify(data, null, 2);
}

// ── CSV (RFC-4180) ───────────────────────────────────────────────────────────────
function csvField(s: string): string {
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
const BOM = String.fromCharCode(0xfeff); // so Excel reads the UTF-8 CSV (accented FR labels) correctly
function toCsv(data: ExportData): string {
  const header = COLUMNS.join(",");
  const lines = data.rows.map((r) => COLUMNS.map((c) => csvField(cell(r, c))).join(","));
  return `${BOM}${[header, ...lines].join("\r\n")}\r\n`;
}

// ── XLSX (SpreadsheetML 2003 XML — Excel-native, no dependency) ────────────────────
function xmlEscape(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
const NUMERIC: ReadonlySet<Col> = new Set(["value", "count"]);
function xmlCell(row: ExportRow, col: Col): string {
  const raw = cell(row, col);
  if (NUMERIC.has(col) && raw !== "") {
    return `<Cell><Data ss:Type="Number">${xmlEscape(raw)}</Data></Cell>`;
  }
  return `<Cell><Data ss:Type="String">${xmlEscape(raw)}</Data></Cell>`;
}
function toXlsx(data: ExportData): string {
  const head = COLUMNS.map((c) => `<Cell><Data ss:Type="String">${c}</Data></Cell>`).join("");
  const body = data.rows
    .map((r) => `<Row>${COLUMNS.map((c) => xmlCell(r, c)).join("")}</Row>`)
    .join("");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<?mso-application progid="Excel.Sheet"?>` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
    `<Worksheet ss:Name="analytics"><Table><Row>${head}</Row>${body}</Table></Worksheet>` +
    `</Workbook>`
  );
}

interface Serialised {
  content: string;
  mime: string;
  ext: string;
}

/** Serialise the export payload into the chosen format (content + mime + extension). */
export function serialiseExport(data: ExportData, format: ExportFormat): Serialised {
  switch (format) {
    case "json":
      return { content: toJson(data), mime: "application/json;charset=utf-8", ext: "json" };
    case "csv":
      return { content: toCsv(data), mime: "text/csv;charset=utf-8", ext: "csv" };
    case "xlsx":
    default:
      return { content: toXlsx(data), mime: "application/vnd.ms-excel;charset=utf-8", ext: "xls" };
  }
}

/** A stable, filesystem-safe filename for the download (lab id + the generated_at date + format). */
export function exportFilename(data: ExportData, ext: string): string {
  const day = (data.generated_at || new Date().toISOString()).slice(0, 10);
  return `analytics_${data.laboratoire_id}_${day}.${ext}`;
}

/**
 * Trigger a browser download of the serialised export. Pure side-effect (Blob + object URL + a transient
 * anchor click); the caller owns the gating + the audited fetch. `doc` is injectable for unit tests.
 */
export function downloadExport(
  data: ExportData,
  format: ExportFormat,
  doc: Document = document,
): void {
  const { content, mime, ext } = serialiseExport(data, format);
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = doc.createElement("a");
  a.href = url;
  a.download = exportFilename(data, ext);
  doc.body.appendChild(a);
  a.click();
  doc.body.removeChild(a);
  URL.revokeObjectURL(url);
}
